/**
 * <cart-upsell> — one-product-per-slide upsell carousel inside the cart drawer.
 *
 * Markup contract (provided by snippets/cart-upsell.liquid):
 *   <cart-upsell>
 *     <div class="cart-upsell__inner">
 *       <div class="cart-upsell__heading-row">
 *         <h3 …>Heading</h3>
 *         <div class="cart-upsell__nav">
 *           <button data-cart-upsell-prev>…</button>
 *           <button data-cart-upsell-next>…</button>
 *         </div>
 *       </div>
 *       <div data-cart-upsell-viewport>
 *         <ul data-cart-upsell-track>
 *           <li data-cart-upsell-slide>
 *             …
 *             <button data-cart-upsell-add
 *                     data-variant-id="…"
 *                     data-product-id="…"
 *                     data-product-title="…">Add to cart</button>
 *           </li>
 *         </ul>
 *       </div>
 *       <span data-cart-upsell-status …></span>
 *     </div>
 *   </cart-upsell>
 *
 * Behaviour:
 *   - Prev / next buttons translate the track horizontally one slide at a time.
 *   - "Add to cart" button posts the variant id to /cart/add.js, then dispatches
 *     CartAddEvent + CartUpdateEvent so the existing cart-drawer +
 *     cart-items-component refresh themselves (no manual DOM patching here —
 *     follows the same contract used by product-form.js elsewhere in the theme).
 *   - aria-busy toggled on the button during the request.
 *   - Polite live region announces success / failure for screen readers.
 *
 * Does NOT touch cart-drawer.js, product-form.js, or any other existing module.
 */
(() => {
  /** @type {typeof import('@theme/events').CartAddEvent | undefined} */
  let CartAddEventCtor;
  /** @type {typeof import('@theme/events').CartUpdateEvent | undefined} */
  let CartUpdateEventCtor;

  // Lazy-load the theme event classes so this script remains a plain <script>
  // (loadable via defer) and gracefully degrades if the events module isn't
  // present yet. The custom element will fall back to a generic CustomEvent.
  const loadThemeEvents = async () => {
    if (CartAddEventCtor && CartUpdateEventCtor) return;
    try {
      const events = await import('@theme/events');
      CartAddEventCtor = events.CartAddEvent;
      CartUpdateEventCtor = events.CartUpdateEvent;
    } catch (_err) {
      // Importmap not resolved or events module unavailable — proceed without.
    }
  };

  class CartUpsell extends HTMLElement {
    #currentIndex = 0;
    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;

      this.viewport = this.querySelector('[data-cart-upsell-viewport]');
      this.track = this.querySelector('[data-cart-upsell-track]');
      this.slides = Array.from(this.querySelectorAll('[data-cart-upsell-slide]'));
      this.prevBtn = this.querySelector('[data-cart-upsell-prev]');
      this.nextBtn = this.querySelector('[data-cart-upsell-next]');
      this.statusRegion = this.querySelector('[data-cart-upsell-status]');

      if (!this.track || this.slides.length === 0) {
        // Nothing to slide / no products — hide nav and bail.
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
        return;
      }

      this.prevBtn?.addEventListener('click', () => this.#go(this.#currentIndex - 1), { signal });
      this.nextBtn?.addEventListener('click', () => this.#go(this.#currentIndex + 1), { signal });

      this.querySelectorAll('[data-cart-upsell-add]').forEach((btn) => {
        btn.addEventListener('click', (event) => this.#handleAdd(event), { signal });
      });

      // Touch-friendly: also support keyboard arrow keys on the viewport.
      this.viewport?.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.#go(this.#currentIndex + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.#go(this.#currentIndex - 1);
          }
        },
        { signal }
      );

      // Re-clamp on resize (slide widths are based on viewport width).
      window.addEventListener('resize', this.#updateTrack, { signal, passive: true });

      this.#updateNavState();

      // Preload event classes in the background — no blocking.
      loadThemeEvents();
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    #go = (target) => {
      const last = this.slides.length - 1;
      let next = target;
      if (next < 0) next = 0;
      if (next > last) next = last;
      if (next === this.#currentIndex) return;
      this.#currentIndex = next;
      this.#updateTrack();
      this.#updateNavState();
    };

    #updateTrack = () => {
      if (!this.track) return;
      const offset = this.#currentIndex * -100;
      this.track.style.transform = `translateX(${offset}%)`;
    };

    #updateNavState = () => {
      const last = this.slides.length - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.#currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.#currentIndex >= last;
    };

    /**
     * @param {Event} event
     */
    #handleAdd = async (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const productId = button.dataset.productId || '';
      const productTitle = button.dataset.productTitle || '';

      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      const originalLabel = button.querySelector('.cart-upsell__add-label')?.textContent;

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        // Pass the variant's Baseline Price as a hidden line-item property.
        const baselinePrice = button.dataset.baselinePrice;
        if (baselinePrice) {
          formData.append('properties[_baseLinePrice]', baselinePrice);
        }

        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        if (!response.ok) {
          let message = 'Failed to add to cart';
          try {
            const errorPayload = await response.json();
            if (errorPayload && errorPayload.description) message = errorPayload.description;
          } catch (_ignored) {
            /* fall through */
          }
          throw new Error(message);
        }

        // Notify the rest of the theme so existing cart components refresh
        // themselves. CartUpdateEvent triggers cart-items rendering;
        // CartAddEvent lets the drawer auto-open if configured.
        await loadThemeEvents();
        if (CartUpdateEventCtor) {
          this.dispatchEvent(new CartUpdateEventCtor({}, 'cart-upsell', { source: 'cart-upsell' }));
        } else {
          this.dispatchEvent(
            new CustomEvent('cart:update', { bubbles: true, detail: { source: 'cart-upsell' } })
          );
        }
        if (CartAddEventCtor) {
          this.dispatchEvent(new CartAddEventCtor({}, productId, { source: 'cart-upsell' }));
        } else {
          this.dispatchEvent(
            new CustomEvent('cart:add', {
              bubbles: true,
              detail: { productId, source: 'cart-upsell' },
            })
          );
        }

        // Visual feedback on the button.
        const labelEl = button.querySelector('.cart-upsell__add-label');
        if (labelEl) labelEl.textContent = 'Added';

        // Announce to screen readers.
        if (this.statusRegion) {
          this.statusRegion.textContent = productTitle
            ? `${productTitle} added to cart`
            : 'Added to cart';
        }

        // Reset label after a moment.
        setTimeout(() => {
          if (labelEl && originalLabel) labelEl.textContent = originalLabel;
          if (this.statusRegion) this.statusRegion.textContent = '';
        }, 1800);
      } catch (_err) {
        if (this.statusRegion) {
          this.statusRegion.textContent = 'Could not add to cart. Please try again.';
        }
        const labelEl = button.querySelector('.cart-upsell__add-label');
        if (labelEl && originalLabel) labelEl.textContent = originalLabel;
      } finally {
        button.setAttribute('aria-busy', 'false');
      }
    };
  }

  if (!customElements.get('cart-upsell')) {
    customElements.define('cart-upsell', CartUpsell);
  }
})();
